import { useState, useEffect, useRef } from "react";
import { X, Send, Loader2, CheckCircle2, Mail, Globe, Building2, Sparkles } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { saveLeadToFirestore } from "@/lib/firebase";
import { toast } from "@/components/ui/sonner";

export function LeadCaptureModal({ isOpen, onClose, websiteUrl, industry, recommendedTools }) {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState(websiteUrl || "");
    const [industryInput, setIndustryInput] = useState(industry || "");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const recaptchaRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setWebsite(websiteUrl || "");
            setIndustryInput(industry || "");
            setSubmitted(false);
            setEmail("");
        }
    }, [isOpen, websiteUrl, industry]);

    // Prevent background scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !email.includes("@")) {
            toast.error("Please enter a valid email address");
            return;
        }

        setSubmitting(true);
        // Read token from the completed visible CAPTCHA widget
        const token = recaptchaRef.current ? recaptchaRef.current.getValue() : null;
        if (!token) {
            toast.error("Please complete the reCAPTCHA check.");
            setSubmitting(false);
            return;
        }

        try {
            const toolNames = recommendedTools?.map((t) => t.name) || [];

            // 1. Save to Supabase (via our helper)
            const result = await saveLeadToFirestore({
                email: email.trim(),
                website: website.trim(),
                industry: industryInput.trim(),
                recommended_tools: toolNames,
            });

            if (!result.success) {
                toast.error("Something went wrong saving your data. Please try again.");
                return;
            }

            // 2. Call Backend to Send Email (Resend)
            // Use environment variable for backend URL, fallback for local dev
            const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

            await fetch(`${backendUrl}/api/capture-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: email.trim(),
                    website: website.trim(),
                    industry: industryInput.trim(),
                    recommended_tools: toolNames,
                    recaptcha_token: token,
                }),
            });

            setSubmitted(true);
            toast.success("Report sent! Check your inbox.");
        } catch (err) {
            console.error("Submission error:", err);
            // If the database save succeeded but email failed, we still show success state
            // because the user's data is captured.
            setSubmitted(true);
            toast.success("Report queued! Check your inbox shortly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-md animate-fade-in"
                onClick={onClose}
                style={{ animationFillMode: "forwards" }}
            />

            {/* Modal */}
            <div
                className="relative w-full max-w-lg animate-scale-in opacity-0"
                style={{ animationFillMode: "forwards" }}
            >
                <div className="relative liquid-glass rounded-3xl shadow-2xl shadow-blue-900/10 overflow-hidden">
                    {/* Top gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500/60 via-cyan-400/60 to-violet-500/60" />

                    {/* Glow effect behind icon */}
                    <div className="absolute top-12 left-1/2 -translate-x-1/2 w-48 h-48 bg-blue-500/8 rounded-full blur-3xl pointer-events-none" />

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors duration-300 p-2 rounded-xl hover:bg-white/5 z-10"
                        data-testid="modal-close"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="p-8 pt-10">
                        {submitted ? (
                            /* Success state */
                            <div className="text-center py-8 animate-fade-in-up opacity-0" style={{ animationFillMode: "forwards" }}>
                                <div className="w-20 h-20 mx-auto mb-6 rounded-3xl glass-icon flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                                </div>
                                <h3 className="text-xl font-semibold tracking-tight text-white mb-2">
                                    You're All Set!
                                </h3>
                                <p className="text-sm text-zinc-400 mb-6 max-w-xs mx-auto leading-relaxed">
                                    Your personalized AI automation report has been queued. Check your inbox shortly.
                                </p>
                                <button
                                    onClick={onClose}
                                    className="flat-btn-ghost text-sm px-8 py-3 rounded-full"
                                >
                                    View My Stack
                                </button>
                            </div>
                        ) : (
                            /* Form state */
                            <>
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 mx-auto mb-5 rounded-2xl glass-icon flex items-center justify-center">
                                        <Sparkles className="w-8 h-8 text-blue-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold tracking-tight text-white mb-2">
                                        Your AI Automation Report is Ready
                                    </h3>
                                    <p className="text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">
                                        Enter your details below and we'll send you a full customized report with ROI projections.
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Email */}
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                                            <Mail className="w-3.5 h-3.5" />
                                            Email Address
                                            <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            data-testid="lead-email-input"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@company.com"
                                            required
                                            className="flat-input w-full h-12 px-4 text-sm"
                                        />
                                    </div>

                                    {/* Website */}
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                                            <Globe className="w-3.5 h-3.5" />
                                            Website
                                        </label>
                                        <input
                                            data-testid="lead-website-input"
                                            type="text"
                                            value={website}
                                            onChange={(e) => setWebsite(e.target.value)}
                                            placeholder="yourwebsite.com"
                                            className="flat-input w-full h-12 px-4 text-sm"
                                        />
                                    </div>

                                    {/* Industry */}
                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">
                                            <Building2 className="w-3.5 h-3.5" />
                                            Industry
                                        </label>
                                        <input
                                            data-testid="lead-industry-input"
                                            type="text"
                                            value={industryInput}
                                            onChange={(e) => setIndustryInput(e.target.value)}
                                            placeholder="e.g. Dental, Real Estate, SaaS"
                                            className="flat-input w-full h-12 px-4 text-sm"
                                        />
                                    </div>

                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        size="normal"
                                        theme="dark"
                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                        className="mt-1"
                                    />

                                    {/* Submit */}
                                    <button
                                        data-testid="lead-submit-button"
                                        type="submit"
                                        disabled={submitting}
                                        className="flat-btn-primary w-full h-12 rounded-xl justify-center mt-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                Send My Report
                                            </>
                                        )}
                                    </button>
                                </form>

                                <p className="text-center text-[11px] text-zinc-700 mt-4">
                                    We respect your privacy. No spam, ever.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
